/**
 * ProviderBadge — which tracker an element belongs to, in that tracker's own
 * brand colours (Voluum magenta, Binom emerald, Tres Commas amber). Extracted from the campaign
 * picker so every surface that mixes trackers marks them the SAME way:
 * once the colour means "Voluum" in one place, it has to mean it everywhere.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { parseTrackerId, trackerLabel, type TrackerId } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

export type BadgeProvider = TrackerId;

/** Brand tones, readable on both themes. Exported so other provider chips (API Credits) reuse the SAME hue per tracker. */
export const PROVIDER_TONE: Record<BadgeProvider, string> = {
  voluum: 'border-pink-500/40 bg-pink-500/10 text-pink-600 dark:text-pink-300',
  binom:
    'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  'tres-commas': 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300',
};

/**
 * The exact geometry of a badge. Exported so a sibling control ("All") can be
 * built from the SAME box — matching padding by hand is how the three pills
 * ended up a pixel apart and visibly unaligned.
 */
export const PROVIDER_BADGE_BASE =
  'inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase leading-none tracking-wide';

/** Rounded-pill geometry — the Scheduler (Optimizer Control Center) chip. */
export const PROVIDER_PILL_BASE =
  'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-1.5 py-px text-[10px] font-medium';

/** Filled variants for a selected state (filter pills). */
const SOLID: Record<BadgeProvider, string> = {
  voluum: 'border-pink-500 bg-pink-500 text-white',
  binom: 'border-emerald-600 bg-emerald-600 text-white',
  'tres-commas': 'border-amber-600 bg-amber-600 text-white',
};

export interface ProviderBadgeProps {
  provider: BadgeProvider;
  /** Solid fill — use when the badge doubles as a selected control. */
  solid?: boolean;
  /** Rounded-pill geometry (mixed-case label) — the Scheduler chip look. */
  pill?: boolean;
  /** What the badge is labelling, for screen readers ("campaign", "element"). */
  noun?: string;
  className?: string;
}

export const ProviderBadge: FC<ProviderBadgeProps> = ({
  provider,
  solid,
  pill,
  noun = 'element',
  className,
}) => (
  <span
    className={cn(
      pill ? PROVIDER_PILL_BASE : PROVIDER_BADGE_BASE,
      solid ? SOLID[provider] : PROVIDER_TONE[provider],
      className,
    )}
    aria-label={`${trackerLabel(provider)} ${noun}`}
  >
    {trackerLabel(provider)}
  </span>
);

export const providerLabel = (provider: BadgeProvider): string => trackerLabel(provider);

/** Narrow a stored platform string to a badge provider, or null when unknown. */
export const asBadgeProvider = (
  platform: string | null | undefined,
): BadgeProvider | null => parseTrackerId(platform);

export default ProviderBadge;
